<!--
  @component TriggerIO — P2 effecting lens (Decision A).
  chain:   ctx.deployment.dispatch("runTrigger", spec) → this → ctx.trigger
  reads:   the parent stream filtered to runTrigger events
  effects: dispatches "runTrigger" through the command port; unwired
           ⇒ typed NotWiredError (FACE-LEDGER, never a fake result).

  THE EFFECTING-LENS TEMPLATE — the pattern P3's 8 arc portals
  instantiate mechanically: local `$state` for in-flight/last,
  an async action that wraps `parent.dispatch(op, payload)`,
  a stream-filtered `history` getter.
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { TRIGGER_IO_KEY } from "./deployment-io.js";
  import type { TriggerIOContext } from "./deployment-io.js";

  export { TRIGGER_IO_KEY };
  export type { TriggerIOContext };

  export function useTriggerIO(): TriggerIOContext {
    const ctx = getContext<TriggerIOContext | undefined>(TRIGGER_IO_KEY);
    if (!ctx) {
      throw new Error("useTriggerIO() called outside of a TriggerIO tree.");
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { SupplyRunSpec } from "./deployment-io.js";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function trigger(spec: SupplyRunSpec): Promise<unknown> {
    inFlight = true;
    lastError = null;
    try {
      const r = await parent.dispatch("runTrigger", spec);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally {
      inFlight = false;
    }
  }

  const ctx: TriggerIOContext = {
    get inFlight() {
      return inFlight;
    },
    get lastResult() {
      return lastResult;
    },
    get lastError() {
      return lastError;
    },
    get history() {
      return parent.stream.filter((e) => e.op === "runTrigger");
    },
    trigger,
  } satisfies TriggerIOContext;

  setContext(TRIGGER_IO_KEY, ctx);
</script>

{@render children?.()}
